/**
 * DataBest — Mobile-first Task Scoring Form (F3, revised in F3.1).
 *
 * Improvements in this revision:
 *  - Prefills the form when re-entering a task already measured today,
 *    so caregivers can correct a previous entry.
 *  - Uses PATCH when an existing measurement is found (auto-versioning
 *    handled by backend), otherwise POST.
 *  - Runs one final synchronous prohibited-word validation right before
 *    save to eliminate the 400ms debounce window.
 *  - Bubbles `saved` state upward with the measurement so parent can
 *    update counters immediately without a full refetch.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Smile, Star, Target, CheckCircle2, ArrowRight, Loader2, AlertTriangle, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { scoringApi } from "@/lib/scoringApi";
import { formatDateUS } from "@/lib/dates";
import VoiceRecorder from "./VoiceRecorder";

const DIMS = [
    { key: "disposition_score", label: "Disposition", icon: Smile },
    { key: "quality_score", label: "Quality", icon: Star },
    { key: "autonomy_score", label: "Autonomy", icon: Target },
];
const SCORE_COLORS = {
    H: { bg: "bg-brand-green-600", border: "border-brand-green-600", text: "text-white" },
    M: { bg: "bg-amber-500", border: "border-amber-500", text: "text-white" },
    L: { bg: "bg-red-600", border: "border-red-600", text: "text-white" },
};

const ScoreButtons = ({ value, onChange, testidPrefix }) => (
    <div className="grid grid-cols-3 gap-2" translate="no">
        {["H", "M", "L"].map((v) => {
            const active = value === v;
            const c = SCORE_COLORS[v];
            return (
                <button
                    key={v}
                    type="button"
                    data-testid={`${testidPrefix}-${v}`}
                    onClick={() => {
                        onChange(v);
                        if (navigator.vibrate) navigator.vibrate(20);
                    }}
                    className={`flex h-16 items-center justify-center rounded-2xl border-2 text-2xl font-black transition-all ${
                        active
                            ? `${c.bg} ${c.border} ${c.text} shadow-md scale-[1.02]`
                            : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                    }`}
                >
                    {v}
                </button>
            );
        })}
    </div>
);

// Small chip palette for the mini-preview in Review mode (score of record).
const CHIP_COLORS = {
    H: "bg-brand-green-100 text-brand-green-800 border-brand-green-200",
    M: "bg-amber-100 text-amber-800 border-amber-200",
    L: "bg-red-100 text-red-800 border-red-200",
};

const formatTime = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_) {
        return "";
    }
};

const SavedSnapshotChips = ({ snapshot }) => {
    const items = [
        { key: "D", value: snapshot.disposition_score },
        { key: "Q", value: snapshot.quality_score },
        { key: "A", value: snapshot.autonomy_score },
    ].filter((c) => c.value);
    const rts = snapshot.response_to_supports;
    const time = formatTime(snapshot.measured_at);
    return (
        <div
            className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold"
            data-testid="saved-snapshot-chips"
        >
            {items.map((c) => (
                <span
                    key={c.key}
                    className={`inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 tracking-wide ${
                        CHIP_COLORS[c.value] ||
                        "bg-slate-100 text-slate-700 border-slate-200"
                    }`}
                    data-testid={`snapshot-chip-${c.key}`}
                >
                    <span className="opacity-60">{c.key}</span>
                    <span className="font-black">{c.value}</span>
                </span>
            ))}
            {(rts === true || rts === false) && (
                <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 tracking-wide ${
                        rts
                            ? "bg-brand-green-100 text-brand-green-800 border-brand-green-200"
                            : "bg-red-100 text-red-800 border-red-200"
                    }`}
                    data-testid="snapshot-chip-RTS"
                >
                    {rts ? "Yes" : "No"}
                </span>
            )}
            {time && (
                <span
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-600"
                    data-testid="snapshot-chip-time"
                >
                    <Clock className="h-3 w-3" />
                    {time}
                </span>
            )}
        </div>
    );
};

// Urgency banner shown when scoring a task coming from the backlog.
// Highlights the overdue level, days overdue, expected due date, and last-measured date.
const LEVEL_STYLES = {
    critical: {
        wrap: "border-red-300 bg-red-50",
        chip: "bg-red-600 text-white",
        label: "Critical",
    },
    overdue: {
        wrap: "border-orange-300 bg-orange-50",
        chip: "bg-orange-500 text-white",
        label: "Overdue",
    },
    due: {
        wrap: "border-amber-300 bg-amber-50",
        chip: "bg-amber-500 text-white",
        label: "Due",
    },
};

const formatDate = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
    } catch (_) {
        return "";
    }
};

const OverdueContextBanner = ({ meta }) => {
    if (!meta) return null;
    const style = LEVEL_STYLES[meta.level] || LEVEL_STYLES.due;
    const days = meta.days_overdue;
    const never = meta.last_measured_at == null;

    let overdueText;
    if (never) {
        overdueText = "Never measured";
    } else if (days < 1 / 24) {
        overdueText = "Just came due";
    } else if (days < 1) {
        const h = Math.round(days * 24);
        overdueText = `${h}h overdue`;
    } else {
        const d = Math.floor(days);
        overdueText = `${d} day${d !== 1 ? "s" : ""} overdue`;
    }

    // Expected due date = last_measured_at + interval_days (in ms).
    let dueDateText = null;
    if (meta.last_measured_at && meta.interval_days != null) {
        try {
            const lastMs = new Date(meta.last_measured_at).getTime();
            const dueMs = lastMs + meta.interval_days * 86400_000;
            dueDateText = formatDate(new Date(dueMs).toISOString());
        } catch (_) {
            dueDateText = null;
        }
    }

    return (
        <div
            className={`mt-3 rounded-xl border-2 ${style.wrap} p-3`}
            data-testid="overdue-context-banner"
        >
            <div className="flex items-center gap-2">
                <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${style.chip}`}
                >
                    {style.label}
                </span>
                <span
                    className="text-xs font-bold text-slate-800"
                    data-testid="overdue-days"
                >
                    {overdueText}
                </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                {dueDateText && (
                    <span data-testid="overdue-due-date">
                        Was due:{" "}
                        <span className="font-semibold text-slate-800">
                            {dueDateText}
                        </span>
                    </span>
                )}
                {meta.last_measured_at && (
                    <span data-testid="overdue-last-measured">
                        Last measured:{" "}
                        <span className="font-semibold text-slate-800">
                            {formatDate(meta.last_measured_at)}
                        </span>
                    </span>
                )}
                {never && (
                    <span
                        className="font-semibold text-red-800"
                        data-testid="overdue-never-measured"
                    >
                        This task has never been scored
                    </span>
                )}
            </div>
        </div>
    );
};

const buildMeasuredAt = (backfillDate) => {
    if (!backfillDate) return new Date().toISOString();
    // Anchor the ISO timestamp to the slot's calendar date at noon UTC.
    // Noon avoids any timezone-driven day-drift when the caregiver is in
    // extreme +/-12h offsets and lets the row surface at a sensible time
    // in downstream reports.
    return new Date(`${backfillDate}T12:00:00Z`).toISOString();
};

const ScoringForm = ({ item, onSaved, onSkip, readOnly = false }) => {
    const [scores, setScores] = useState({});
    const [rts, setRts] = useState(null);
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);
    const [scan, setScan] = useState(null);
    // Existing measurement (if any) for this task today — used to switch POST→PATCH.
    const [existingMeasurementId, setExistingMeasurementId] = useState(null);
    // Persist saved measured_at + saved-score snapshot so the mini-preview
    // in the header keeps showing the DB-of-record values while the user
    // may be editing the scores.
    const [savedSnapshot, setSavedSnapshot] = useState(null);
    const [prefilling, setPrefilling] = useState(false);

    // Reset + prefill whenever the active task changes.
    useEffect(() => {
        const taskId = item?.task?.id;
        if (!taskId) return;
        setScores({});
        setRts(null);
        setComment("");
        setScan(null);
        setExistingMeasurementId(null);
        setSavedSnapshot(null);

        // If backend told us this task was measured today, fetch the current
        // measurement so the caregiver can correct it.
        if (item.measured_today) {
            setPrefilling(true);
            scoringApi
                .list({ task_id: taskId, is_current: true })
                .then((rows) => {
                    const current = (rows || [])
                        .filter((r) => r.is_current)
                        .sort((a, b) =>
                            (b.measured_at || "").localeCompare(a.measured_at || ""),
                        )[0];
                    if (!current) return;
                    setExistingMeasurementId(current.id);
                    setScores({
                        disposition_score: current.disposition_score,
                        quality_score: current.quality_score,
                        autonomy_score: current.autonomy_score,
                    });
                    setRts(
                        current.response_to_supports === true ||
                            current.response_to_supports === false
                            ? current.response_to_supports
                            : null,
                    );
                    setComment(current.comment_text || current.transcription || "");
                    setSavedSnapshot({
                        disposition_score: current.disposition_score,
                        quality_score: current.quality_score,
                        autonomy_score: current.autonomy_score,
                        response_to_supports: current.response_to_supports,
                        measured_at: current.measured_at,
                    });
                })
                .finally(() => setPrefilling(false));
        }
    }, [item?.task?.id]);

    const validate = async (text) => {
        if (!text.trim()) {
            setScan(null);
            return { blocked: false, hits: [], preferred_used: [] };
        }
        try {
            const r = await scoringApi.validateText(text);
            setScan(r);
            return r;
        } catch (err) {
            // Fail-closed: never let a network hiccup allow a prohibited word through.
            const failed = {
                blocked: true,
                hits: [],
                preferred_used: [],
                _error: err?.message || "Validation failed",
            };
            setScan(failed);
            toast.error(
                "Could not verify comment. Please retry before saving.",
            );
            return failed;
        }
    };

    const handleCommentChange = (v) => {
        setComment(v);
        // Debounced background scan; a final synchronous scan runs on save.
        clearTimeout(handleCommentChange._t);
        handleCommentChange._t = setTimeout(() => validate(v), 400);
    };

    const applySuggestion = (word) => {
        if (!scan?.hits?.length) return;
        const hit = scan.hits[0];
        const next = comment.slice(0, hit.start) + word + comment.slice(hit.end);
        setComment(next);
        validate(next);
    };

    const handleVoice = async (text) => {
        const trimmed = (text || "").trim();
        if (!trimmed) {
            toast.info("No speech detected — please type your comment");
            return;
        }
        // Reveal the transcript first so the user sees exactly what was heard.
        setComment(trimmed);
        // Await the scan so we can announce the correct outcome instead of
        // showing a premature "transcribed" success.
        const result = await validate(trimmed);
        if (result?.blocked || result?.hits?.length) {
            toast.error(
                `Prohibited word detected: "${
                    result.hits?.[0]?.matched_text || trimmed
                }". Please revise.`,
            );
        } else {
            toast.success("Voice transcribed");
        }
    };

    const canSave =
        scores.disposition_score &&
        scores.quality_score &&
        scores.autonomy_score &&
        rts !== null;

    const isEditing = !!existingMeasurementId;

    const handleSave = async () => {
        if (!canSave) {
            toast.warning("Please rate all 3 dimensions and select Yes/No");
            return;
        }
        // Final synchronous validation to close the 400ms debounce gap.
        const finalScan = await validate(comment);
        if (finalScan.blocked) {
            toast.error("Please fix prohibited words before saving");
            return;
        }
        setSaving(true);
        try {
            const body = {
                task_id: item.task.id,
                individual_id: item.individual_id,
                measured_at: buildMeasuredAt(item.backfill_date),
                disposition_score: scores.disposition_score,
                quality_score: scores.quality_score,
                autonomy_score: scores.autonomy_score,
                response_to_supports: rts,
                comment_text: comment || null,
            };
            const resp = existingMeasurementId
                ? await scoringApi.update(existingMeasurementId, body)
                : await scoringApi.create(body);
            // Refresh the mini-preview snapshot with the just-persisted values so
            // if the user navigates back via Previous it shows the new state.
            const persisted = resp?.measurement;
            if (persisted) {
                setExistingMeasurementId(persisted.id);
                setSavedSnapshot({
                    disposition_score: persisted.disposition_score,
                    quality_score: persisted.quality_score,
                    autonomy_score: persisted.autonomy_score,
                    response_to_supports: persisted.response_to_supports,
                    measured_at: persisted.measured_at,
                });
            }
            toast.success(isEditing ? "Updated" : "Saved");
            onSaved?.(persisted);
        } catch (err) {
            if (err?.data?.detail?.hits) {
                setScan({ blocked: true, hits: err.data.detail.hits });
                toast.error("Please fix prohibited words before saving");
            } else {
                toast.error(err?.message || "Save failed");
            }
        } finally {
            setSaving(false);
        }
    };

    const buttonLabel = useMemo(() => {
        if (saving) return null;
        return isEditing ? "Update & Next" : "Save & Next";
    }, [saving, isEditing]);

    if (!item) return null;
    const t = item.task;

    return (
        <fieldset
            disabled={readOnly}
            className="mx-auto w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-5 shadow-lg md:p-8 disabled:opacity-100"
            data-testid="scoring-form"
        >
            {readOnly && (
                <div
                    data-testid="scoring-form-readonly-banner"
                    className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600"
                >
                    Read only · viewing another caregiver&apos;s entry
                </div>
            )}
            {/* Task header */}
            <div className="mb-5 border-b border-slate-100 pb-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-blue-700">
                    {item.outcome.life_area || "—"} · {item.outcome.title.slice(0, 60)}
                </p>
                <h2
                    className="mt-1 font-heading text-xl font-bold text-brand-blue-950"
                    data-testid="scoring-task-title"
                >
                    {t.title}
                </h2>
                {item.backlog && <OverdueContextBanner meta={item.backlog} />}
                {item.backfill_date && (
                    <div
                        data-testid="scoring-form-backfill-chip"
                        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-800"
                    >
                        Backfilling entry for {formatDateUS(item.backfill_date)}
                    </div>
                )}
                {isEditing && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <p
                            className="inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-800"
                            data-testid="editing-badge"
                        >
                            Editing today&apos;s entry
                        </p>
                        {savedSnapshot && (
                            <SavedSnapshotChips snapshot={savedSnapshot} />
                        )}
                    </div>
                )}
                {t.support_instructions && (
                    <details className="mt-2 text-sm text-slate-600">
                        <summary className="cursor-pointer font-semibold text-brand-blue-700">
                            Show instructions
                        </summary>
                        <p className="mt-2 whitespace-pre-wrap">
                            {t.support_instructions}
                        </p>
                    </details>
                )}
            </div>

            {prefilling && (
                <div className="mb-3 flex items-center gap-2 text-xs text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading your previous entry…
                </div>
            )}

            {/* Dimensions */}
            {DIMS.map((d) => (
                <div key={d.key} className="mb-4">
                    <div className="mb-2 flex items-center gap-2">
                        <d.icon className="h-4 w-4 text-brand-blue-700" />
                        <p className="text-sm font-semibold text-slate-800">{d.label}</p>
                    </div>
                    <ScoreButtons
                        value={scores[d.key]}
                        onChange={(v) => setScores((s) => ({ ...s, [d.key]: v }))}
                        testidPrefix={`score-${d.key}`}
                    />
                </div>
            ))}

            {/* Response to Supports */}
            <div className="mb-4">
                <div className="mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-blue-700" />
                    <p className="text-sm font-semibold text-slate-800">
                        Response to Supports
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        data-testid="rts-yes"
                        onClick={() => setRts(true)}
                        className={`h-14 rounded-2xl border-2 text-base font-bold transition-all ${
                            rts === true
                                ? "border-brand-green-600 bg-brand-green-600 text-white"
                                : "border-slate-200 bg-white text-slate-600"
                        }`}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        data-testid="rts-no"
                        onClick={() => setRts(false)}
                        className={`h-14 rounded-2xl border-2 text-base font-bold transition-all ${
                            rts === false
                                ? "border-red-600 bg-red-600 text-white"
                                : "border-slate-200 bg-white text-slate-600"
                        }`}
                    >
                        No
                    </button>
                </div>
            </div>

            {/* Comment + voice */}
            <div className="mb-5">
                <p className="mb-2 text-sm font-semibold text-slate-800">
                    Comment (optional)
                </p>
                <div className="flex items-start gap-3">
                    <textarea
                        value={comment}
                        onChange={(e) => handleCommentChange(e.target.value)}
                        placeholder="Type or tap the mic…"
                        data-testid="comment-input"
                        className="min-h-[80px] flex-1 rounded-xl border border-slate-200 bg-white p-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20"
                    />
                    <VoiceRecorder onTranscript={handleVoice} />
                </div>
                {scan?.hits?.length > 0 && (
                    <div
                        className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900"
                        data-testid="prohibited-warning"
                    >
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="mt-0.5 h-4 w-4" />
                            <div className="flex-1">
                                <p className="font-semibold">
                                    Avoid: &quot;{scan.hits[0].matched_text}&quot;
                                </p>
                                <p className="mt-1 text-xs">
                                    Try one of these preferred alternatives:
                                </p>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {(scan.hits[0].suggestions || []).slice(0, 6).map(
                                        (w) => (
                                            <button
                                                key={w}
                                                type="button"
                                                onClick={() => applySuggestion(w)}
                                                className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-brand-blue-700 hover:bg-brand-blue-50"
                                            >
                                                {w}
                                            </button>
                                        ),
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {scan?.preferred_used?.length > 0 && !scan?.hits?.length && (
                    <p className="mt-2 text-xs text-brand-green-700">
                        ✓ Great language:{" "}
                        {scan.preferred_used.slice(0, 4).join(", ")}
                    </p>
                )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
                <button
                    onClick={handleSave}
                    disabled={saving || scan?.blocked}
                    data-testid="save-next-button"
                    className="flex h-14 items-center justify-center gap-2 rounded-2xl bg-brand-blue-700 text-base font-bold uppercase tracking-wider text-white shadow-md hover:bg-brand-blue-800 disabled:opacity-60"
                >
                    {saving ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                        <>
                            {buttonLabel} <ArrowRight className="h-5 w-5" />
                        </>
                    )}
                </button>
                <button
                    onClick={onSkip}
                    data-testid="skip-button"
                    className="h-11 rounded-xl text-sm font-semibold text-slate-500 hover:text-slate-700"
                >
                    Skip for now
                </button>
            </div>
        </fieldset>
    );
};

export default ScoringForm;
